import { useQuery } from '@tanstack/react-query';
import {
  BarChart, Bar, LineChart, Line, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine,
} from 'recharts';
import { CalendarCheck, CalendarDays, Gift, Palmtree } from 'lucide-react';
import { api } from '../../lib/api';
import { Card, EmptyState } from '../ui';
import {
  KpiCard, KpiRow, ChartCard, TabLoading, TabError, EmptyChart, CHART, ANIM, LEAVE_COLORS, uur1,
  TOOLTIP_STYLE, TOOLTIP_LABEL_STYLE, TOOLTIP_CURSOR, LINE_CURSOR, GRID_STROKE, AXIS_PROPS, LEGEND_PROPS,
  paddedDomain,
} from './StatsShared';
import { formatDate } from '../../lib/utils';

const hoursFmt = uur1;

const LEAVE_LABEL: Record<string, string> = {
  vakantie: 'Vakantie',
  feestdagverlof: 'Feestdagverlof',
  bijzonder_verlof: 'Bijzonder verlof',
  buitengewoon: 'Buitengewoon verlof',
  buitengewoon_verlof: 'Buitengewoon verlof',
  ziekte: 'Ziekte',
};

const STATUS_LABEL: Record<string, string> = {
  approved: 'Goedgekeurd',
  rejected: 'Afgewezen',
  requested: 'Aangevraagd',
};

export function VerlofTab({ year }: { year: number }) {
  const { data, isLoading, error } = useQuery({
    queryKey: ['stats-verlof', year],
    queryFn: () => api.stats.verlof(year),
  });

  if (isLoading) return <TabLoading />;
  if (error || !data) return <TabError error={error} />;

  const opbouwData = data.snapshots.map((s) => ({
    name: `P${s.period}`,
    'Wettelijk opbouw': Math.round(s.wettelijk_opbouw * 100) / 100,
    'Bovenwettelijk opbouw': Math.round(s.bovenwettelijk_opbouw * 100) / 100,
  }));

  const saldoData = data.snapshots.map((s) => ({
    name: `P${s.period}`,
    Wettelijk: Math.round(s.wettelijk_saldo * 100) / 100,
    Bovenwettelijk: Math.round(s.bovenwettelijk_saldo * 100) / 100,
    Feestdag: Math.round(s.feestdagverlof_saldo * 100) / 100,
  }));

  const planned = data.planned.filter((p) => p.status !== 'rejected');
  const consumed = buildCumulativeConsumed(data.consumed);

  return (
    <div className="space-y-4 sm:space-y-6">
      <KpiRow>
        <KpiCard icon={Palmtree} label="Wettelijk saldo" kpi={data.kpis.wettelijk_saldo} fmt="hours" prevYear={year - 1} />
        <KpiCard icon={Palmtree} label="Bovenwettelijk saldo" kpi={data.kpis.bovenwettelijk_saldo} fmt="hours" prevYear={year - 1} />
        <KpiCard icon={Gift} label="Feestdagverlof" kpi={data.kpis.feestdagverlof} fmt="hours" prevYear={year - 1} />
        <KpiCard icon={CalendarCheck} label="Opgenomen dit jaar" kpi={data.kpis.verbruikt_ytd} fmt="hours" prevYear={year - 1} />
      </KpiRow>

      <ChartCard title="Opgebouwd per periode" subtitle="Wettelijke en bovenwettelijke uren">
        {opbouwData.length === 0 ? (
          <EmptyChart>Nog geen verlofgegevens van {year}.</EmptyChart>
        ) : (
          <ResponsiveContainer>
            <BarChart data={opbouwData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={GRID_STROKE} vertical={false} />
              <XAxis dataKey="name" {...AXIS_PROPS} />
              <YAxis {...AXIS_PROPS} width={36} />
              <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} cursor={TOOLTIP_CURSOR} formatter={hoursFmt} />
              <Legend {...LEGEND_PROPS} />
              <Bar dataKey="Wettelijk opbouw" name="Wettelijk" fill={CHART.main} radius={[4, 4, 0, 0]} isAnimationActive={false} />
              <Bar dataKey="Bovenwettelijk opbouw" name="Bovenwettelijk" fill={CHART.muted} radius={[4, 4, 0, 0]} isAnimationActive={false} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard title="Saldo per periode" subtitle="Stand aan het eind van elke periode. Onder nul betekent meer opgenomen dan opgebouwd.">
        {saldoData.length === 0 ? (
          <EmptyChart>Nog geen verlofgegevens van {year}.</EmptyChart>
        ) : (
          <ResponsiveContainer>
            <LineChart data={saldoData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={GRID_STROKE} vertical={false} />
              <XAxis dataKey="name" {...AXIS_PROPS} />
              <YAxis {...AXIS_PROPS} domain={paddedDomain(8)} width={40} />
              <ReferenceLine y={0} stroke={CHART.muted} strokeDasharray="2 2" />
              <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} cursor={LINE_CURSOR} formatter={hoursFmt} />
              <Legend {...LEGEND_PROPS} />
              <Line type="monotone" dataKey="Wettelijk" stroke={CHART.main} strokeWidth={2} dot={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="Bovenwettelijk" stroke={CHART.muted} strokeWidth={2} dot={false} isAnimationActive={false} />
              <Line type="monotone" dataKey="Feestdag" stroke={CHART.verlof} strokeWidth={2} dot={false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <ChartCard title="Opgenomen verlof, opgeteld" height={220}>
        {consumed.length === 0 ? (
          <EmptyChart>Nog geen verlof opgenomen in {year}.</EmptyChart>
        ) : (
          <ResponsiveContainer>
            <AreaChart data={consumed} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke={GRID_STROKE} vertical={false} />
              <XAxis dataKey="date" {...AXIS_PROPS} minTickGap={16} />
              <YAxis {...AXIS_PROPS} width={36} />
              <Tooltip contentStyle={TOOLTIP_STYLE} labelStyle={TOOLTIP_LABEL_STYLE} cursor={LINE_CURSOR} formatter={(v: unknown) => [hoursFmt(v), 'Opgenomen']} />
              <Area type="monotone" dataKey="cum" stroke={CHART.main} strokeWidth={2} fill={CHART.main} fillOpacity={0.06} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </ChartCard>

      <Card flush title="Gepland verlof" className="min-w-0">
        {planned.length === 0 ? (
          <EmptyState icon={CalendarDays} title="Geen verlof gepland of aangevraagd" />
        ) : (
          <div className="list border-t border-surface-line/60">
            {planned.map((p, i) => (
              <div key={i} className="row enter" style={{ '--i': i } as React.CSSProperties}>
                <span className="h-2 w-2 shrink-0 rounded-full" style={{ background: LEAVE_COLORS[p.leave_type] ?? '#8B97A6' }} aria-hidden />
                <div className="min-w-0 flex-1">
                  <div className="t-num truncate text-sm text-slate-100">
                    {formatDate(p.start_date)} t/m {formatDate(p.end_date)}
                  </div>
                  <div className="truncate text-xs text-slate-400">{LEAVE_LABEL[p.leave_type] ?? p.leave_type}</div>
                </div>
                <div className="shrink-0 text-right">
                  <div className="t-num text-sm text-slate-100">{hoursFmt(p.hours)}</div>
                  <span className={statusClass(p.status)}>{STATUS_LABEL[p.status] ?? p.status}</span>
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  );
}

function buildCumulativeConsumed(consumed: Array<{ start_date: string; hours: number }>): Array<{ date: string; cum: number }> {
  let cum = 0;
  return consumed
    .slice()
    .sort((a, b) => a.start_date.localeCompare(b.start_date))
    .map((c) => {
      cum += c.hours ?? 0;
      // dd-mm
      const [, m, d] = c.start_date.split('-');
      return { date: `${d}-${m}`, cum: Math.round(cum * 100) / 100 };
    });
}

function statusClass(status: string): string {
  if (status === 'approved') return 'status-ok';
  if (status === 'rejected') return 'status-err';
  if (status === 'requested') return 'status-warn';
  return 'status-muted';
}
